import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Check, X, AlertCircle, Search, Filter } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// Robust backend URL resolution with multiple fallbacks
const getBackendUrl = () => {
  // Priority 1: Runtime config (window.ENV_CONFIG)
  if (window.ENV_CONFIG?.backendUrl) {
    return window.ENV_CONFIG.backendUrl;
  }
  
  // Priority 2: Build-time environment variable
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // Priority 3: Hard fallback - derive from current origin
  const origin = window.location.origin.replace(/\/$/, '');
  return origin;
};

const BACKEND_URL = getBackendUrl();

// Log the resolved URL for debugging
console.log('[SourceMaster] Resolved BACKEND_URL:', BACKEND_URL);

export default function SourceMaster() {
  const { getAuthHeader } = useAuth();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [syncing, setSyncing] = useState(false);
  
  // Filter states
  const [nameFilter, setNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    is_active: true,
    display_order: 0
  });

  useEffect(() => {
    fetchSources();
  }, []);

  // Use XMLHttpRequest wrapper to bypass rrweb interception
  const safeFetch = async (url, options = {}) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method || 'GET', url);
      
      // Set headers
      if (options.headers) {
        Object.entries(options.headers).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });
      }
      
      xhr.onload = () => {
        const response = {
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          json: () => Promise.resolve(JSON.parse(xhr.responseText)),
          text: () => Promise.resolve(xhr.responseText)
        };
        resolve(response);
      };
      
      xhr.onerror = () => reject(new Error('Network request failed'));
      xhr.send(options.body || null);
    });
  };

  const fetchSources = async () => {
    try {
      const response = await safeFetch(`${BACKEND_URL}/api/sources/`, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch sources');
      }
      
      const data = await response.json();
      setSources(data);
    } catch (error) {
      console.error('Error fetching sources:', error);
      alert('Failed to fetch sources');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let response;
      
      if (editingSource) {
        // Update
        response = await safeFetch(`${BACKEND_URL}/api/sources/${editingSource.id}`, {
          method: 'PUT',
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
      } else {
        // Create
        response = await safeFetch(`${BACKEND_URL}/api/sources/`, {
          method: 'POST',
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
      }
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save source');
      }
      
      alert(editingSource ? 'Source updated successfully' : 'Source created successfully');
      fetchSources();
      closeModal();
    } catch (error) {
      console.error('Error saving source:', error);
      alert(error.message || 'Failed to save source');
    }
  };

  const handleDelete = async (sourceId, sourceName) => {
    const confirmed = await window.confirm(`Are you sure you want to delete "${sourceName}"?`);
    if (!confirmed) {
      return;
    }

    try {
      const response = await safeFetch(`${BACKEND_URL}/api/sources/${sourceId}`, {
        method: 'DELETE',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete source');
      }
      
      alert('Source deleted successfully');
      fetchSources();
    } catch (error) {
      console.error('Error deleting source:', error);
      alert(error.message || 'Failed to delete source');
    }
  };

  const openModal = (source = null) => {
    if (source) {
      setEditingSource(source);
      setFormData({
        name: source.name,
        code: source.code,
        description: source.description || '',
        is_active: source.is_active,
        display_order: source.display_order
      });
    } else {
      setEditingSource(null);
      setFormData({
        name: '',
        code: '',
        description: '',
        is_active: true,
        display_order: sources.length + 1
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSource(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      is_active: true,
      display_order: 0
    });
  };

  const handleSyncSources = async () => {
    const confirmed = await window.confirm('This will import all unique sources from existing leads. Continue?');
    if (!confirmed) {
      return;
    }

    setSyncing(true);
    try {
      const response = await safeFetch(`${BACKEND_URL}/api/admin/sync-sources`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to sync sources');
      }

      const result = await response.json();
      alert(`Sync completed!\n\nAdded: ${result.sources_added}\nSkipped: ${result.sources_skipped}\nTotal: ${result.total_sources}`);
      fetchSources();
    } catch (error) {
      console.error('Error syncing sources:', error);
      alert(error.message || 'Failed to sync sources');
    } finally {
      setSyncing(false);
    }
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  // Filter sources based on filters
  const filteredSources = sources.filter(source => {
    if (nameFilter && !source.name?.toLowerCase().includes(nameFilter.toLowerCase()) && !source.code?.toLowerCase().includes(nameFilter.toLowerCase())) {
      return false;
    }
    if (statusFilter === 'active' && !source.is_active) {
      return false;
    }
    if (statusFilter === 'inactive' && source.is_active) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading sources...</div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-6 flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Source Master</h1>
            <p className="text-gray-600">Manage lead sources for your CRM</p>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={handleSyncSources}
              disabled={syncing}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {syncing ? 'Syncing...' : 'Sync from Leads'}
            </button>
            <button 
              onClick={() => openModal()}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
            >
              <Plus size={18} />
              <span>Add Source</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg shadow-md p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search by Name/Code
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Enter source name or code..."
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setNameFilter('');
                  setStatusFilter('');
                }}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {filteredSources.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">
              {sources.length === 0 ? 'No sources found. Click "Add Source" to create one.' : 'No sources match your filters'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredSources.map((source) => (
                    <tr key={source.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{source.display_order}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">{source.name}</td>
                      <td className="px-6 py-4 text-sm">
                        <code className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-mono">
                          {source.code}
                        </code>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{source.description || '-'}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(source.is_active)}`}>
                          {source.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openModal(source)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit Source"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(source.id, source.name)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Source"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeModal}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-bold text-gray-800">
                {editingSource ? 'Edit Source' : 'Add New Source'}
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Source Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., Facebook"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                    required
                    placeholder="e.g., facebook"
                    disabled={editingSource ? true : false}
                  />
                  <small className="text-gray-500 text-xs">
                    {editingSource ? 'Code cannot be changed after creation' : 'Lowercase, use underscores for spaces'}
                  </small>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    rows="3"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this source"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
                  <input
                    type="number"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                    min="0"
                  />
                  <small className="text-gray-500 text-xs">
                    Lower numbers appear first in dropdowns
                  </small>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                    Active (visible in lead creation forms)
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                >
                  <Check size={18} />
                  <span>{editingSource ? 'Update Source' : 'Create Source'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
